/**
 * Centralized wrapper around the global Infralytiqs tracker SDK that is
 * loaded from https://assets.infralytiqs.com/v1/scripts/infralytiqs.min.js
 * (see public/index.html).
 *
 * All helpers are guarded — if the SDK is not yet on `window` (e.g. blocked,
 * build ran before the asset was copied, or running in a test/SSR env), every
 * call becomes a silent no-op instead of crashing the app.
 *
 * The SDK already captures `session_id`, `anonymous_id`, `utm_*`, device/geo,
 * and `page_url` on every event. We therefore only add the semantic properties
 * that are specific to each user action (e.g. email, template type, plan key).
 */
import { Logger } from './Logger';

type Primitive = string | number | boolean | null | undefined;
type EventMeta = Record<string, Primitive>;

interface InfralytiqsGlobal {
    init?: (config: Record<string, unknown>) => void;
    track: (eventType: string, properties?: Record<string, unknown>) => void;
    identify?: (userId: string, traits?: Record<string, unknown>) => void;
    setUser?: (userId: string, traits?: Record<string, unknown>) => void;
    reset?: () => void;
}

const LOG_PREFIX = 'st-ck-client/src/utils/Infralytiqs.ts';

const getSdk = (): InfralytiqsGlobal | null => {
    if (typeof window === 'undefined') return null;
    const ns = (window as unknown as { Infralytiqs?: InfralytiqsGlobal & { default?: InfralytiqsGlobal } })
        .Infralytiqs;
    if (!ns) return null;
    const api = typeof ns.track === 'function' ? ns : ns.default;
    return api && typeof api.track === 'function' ? api : null;
};

/**
 * One-shot initialization of the global Infralytiqs SDK with values resolved
 * at runtime from `/system/client/config?domainKey=<host>`. Idempotent: safe
 * to call on every clientConfig change — the SDK itself short-circuits
 * duplicate `init()` calls.
 *
 * While this is running asynchronously on app boot, the SDK's internal
 * pre-init buffer captures any early `track()` / `identify()` calls; they
 * are drained once `init()` completes. No events are lost.
 */
export const initInfralytiqs = (opts: {
    serverUrl: string;
    tenantId: string;
    siteId: string;
    dbName?: string;
    debug?: boolean;
    /**
     * When true, the SDK prompts for `navigator.geolocation` consent on init
     * and attaches precise lat/lng to every event. Resolved per-domain from
     * `/system/client/config`. Defaults to false (country-level IP geo only).
     */
    captureLocation?: boolean;
}): void => {
    const sdk = getSdk();
    if (!sdk || typeof sdk.init !== 'function') {
        Logger.debug(LOG_PREFIX, 'initInfralytiqs(): SDK not available, skipping');
        return;
    }
    if (!opts.serverUrl || !opts.tenantId || !opts.siteId) {
        Logger.warn(LOG_PREFIX, 'initInfralytiqs(): serverUrl, tenantId, and siteId are required — skipping');
        return;
    }
    try {
        sdk.init({
            serverUrl: opts.serverUrl,
            tenantId: opts.tenantId,
            siteId: opts.siteId,
            dbName: opts.dbName,
            debug: opts.debug ?? false,
            captureLocation: !!opts.captureLocation,
        });
    } catch (err) {
        Logger.warn(LOG_PREFIX, `initInfralytiqs() failed: ${err}`);
    }
};

/**
 * Send a generic event. Safely no-ops if the SDK is not yet available.
 *
 * `eventType` maps to ClickHouse `event_type` (LowCardinality), so keep it short
 * and stable. Use `event_subtype` for variants (e.g. template kind, plan key).
 */
export const trackEvent = (
    eventType: string,
    properties: Record<string, unknown> = {},
): void => {
    const sdk = getSdk();
    if (!sdk) {
        Logger.debug(LOG_PREFIX, `trackEvent(${eventType}): SDK not available, skipping`);
        return;
    }
    try {
        sdk.track(eventType, properties);
    } catch (err) {
        Logger.warn(LOG_PREFIX, `trackEvent(${eventType}) failed: ${err}`);
    }
};

/**
 * Associate subsequent events with a logged-in user. Uses whichever of the
 * SDK's identification APIs is available. Falls back to a plain user_id on
 * `track()` payloads when the SDK exposes neither.
 */
export const identifyUser = (userId: string, traits: EventMeta = {}): void => {
    const sdk = getSdk();
    if (!sdk) return;
    try {
        if (typeof sdk.identify === 'function') {
            sdk.identify(userId, traits);
        } else if (typeof sdk.setUser === 'function') {
            sdk.setUser(userId, traits);
        }
    } catch (err) {
        Logger.warn(LOG_PREFIX, `identifyUser(${userId}) failed: ${err}`);
    }
};

/** Clear any in-memory user association held by the SDK (best-effort). */
export const resetIdentity = (): void => {
    const sdk = getSdk();
    if (!sdk || typeof sdk.reset !== 'function') return;
    try {
        sdk.reset();
    } catch (err) {
        Logger.warn(LOG_PREFIX, `resetIdentity() failed: ${err}`);
    }
};

// ─── Semantic helpers (one per user-visible journey) ────────────────────────

export const trackLogin = (
    email: string,
    meta: { userId?: string; method?: string; companyId?: string } = {},
): void => {
    // Use the user's email address as the canonical identity for analytics
    // — it's stable across sessions, human-readable in dashboards, and
    // avoids leaking internal MongoDB ObjectIDs into the ClickHouse store.
    // The internal `_id` is still attached as a trait so engineering can
    // correlate analytics rows back to application data when needed.
    const identity = email || meta.userId;
    if (identity) {
        identifyUser(identity, {
            email,
            user_id: meta.userId,
            company_id: meta.companyId,
        });
    }
    trackEvent('user_login', {
        event_subtype: meta.method || 'password',
        email,
        user_email: email,
        mongo_user_id: meta.userId,
        company_id: meta.companyId,
    });
};

export const trackLogout = (
    email?: string,
    meta: { userId?: string; companyId?: string } = {},
): void => {
    trackEvent('user_logout', {
        email,
        user_email: email,
        mongo_user_id: meta.userId,
        company_id: meta.companyId,
    });
    resetIdentity();
};

export const trackPageView = (
    path: string,
    meta: { title?: string; referrer?: string } = {},
): void => {
    trackEvent('page_view', {
        page_path: path,
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        page_title: meta.title ?? (typeof document !== 'undefined' ? document.title : undefined),
        page_referrer: meta.referrer ?? (typeof document !== 'undefined' ? document.referrer : undefined),
    });
};

/**
 * Generic action tracker. Use this for "thing was created / thing happened"
 * user journeys. `entity` ends up as event_type (e.g. "campaign_created"),
 * `subtype` as event_subtype (e.g. "whatsapp"), and the rest becomes
 * custom_dimensions on the ClickHouse row.
 */
export const trackAction = (
    entity: string,
    subtype: string | undefined,
    meta: EventMeta = {},
): void => {
    trackEvent(entity, {
        event_subtype: subtype,
        ...meta,
    });
};
